import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { HospitalLogo, useHospital } from '@/components/HospitalLogo';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { AREA_ICONS, AREAS, NAV_ALIASES, visibleNav, type NavItem } from './nav';
import { HospitalSwitcher, LanguageSwitcher, NotificationsBell, ThemeSwitcher, UserMenu } from './TopbarWidgets';

const NAV_KEY = 'hms.sidebarCollapsed';

/** Sidebar collapsed to icons: the user's last choice, else collapsed on tablet widths. */
function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(NAV_KEY);
      if (saved != null) return saved === '1';
    } catch {
      /* storage unavailable */
    }
    return window.matchMedia('(max-width: 1100px)').matches;
  });
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(NAV_KEY, c ? '0' : '1');
      } catch {
        /* storage unavailable */
      }
      return !c;
    });
  return [collapsed, toggle] as const;
}

function sectionOf(pathname: string) {
  const section = '/' + (pathname.split('/')[1] ?? '');
  return NAV_ALIASES[section] ?? section;
}

/** A sub-link is current when its path matches and its tab (if any) is the open tab. */
function childActive(to: string, pathname: string, search: string, first: boolean) {
  const [path, query = ''] = to.split('?');
  if (pathname !== path) return false;
  const want = new URLSearchParams(query).get('tab');
  const have = new URLSearchParams(search).get('tab');
  return want === have || (!have && first);
}

function NavGroup({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const inSection = sectionOf(location.pathname) === item.to;
  const [open, setOpen] = useState(inSection);
  useEffect(() => {
    if (inSection) setOpen(true);
  }, [inSection]);
  const children = item.children ?? [];
  return (
    <div className={`nav-group ${open ? 'open' : ''}`}>
      <div className="nav-group-head">
        <NavLink
          to={children[0]?.to ?? item.to}
          className={() => `nav-link ${inSection ? 'active' : ''} ${children.length > 1 ? 'has-toggle' : ''}`}
          title={t(item.labelKey)}
          aria-expanded={children.length > 1 ? open : undefined}
          onClick={(e) => {
            // Already in this module: the link opens / closes its sub-links (unless the sidebar shows icons only).
            if (children.length > 1 && inSection && !e.currentTarget.closest('.nav-collapsed')) {
              e.preventDefault();
              setOpen(!open);
              return;
            }
            if (children.length > 1) setOpen(true);
            onNavigate?.();
          }}
        >
          {item.icon}
          <span className="label">{t(item.labelKey)}</span>
        </NavLink>
        {children.length > 1 && (
          <button type="button" className="nav-toggle" aria-expanded={open} aria-label={t(item.labelKey)} onClick={() => setOpen(!open)}>
            <ChevronDown size={16} />
          </button>
        )}
      </div>
      {open && children.length > 1 && (
        <div className="nav-children">
          {children.map((c, i) => (
            <Link
              key={c.to}
              to={c.to}
              className={`nav-child ${childActive(c.to, location.pathname, location.search, i === 0) ? 'active' : ''}`}
              onClick={onNavigate}
            >
              {c.icon}
              <span>{t(c.labelKey)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { canAny } = useAuth();
  const location = useLocation();
  const items = visibleNav(canAny);
  // Only the most specific link is active (/patients/duplicates, not also /patients).
  const current = items
    .map((i) => i.to)
    .filter((to) => (to === '/' ? location.pathname === '/' : location.pathname === to || location.pathname.startsWith(`${to}/`)))
    .sort((a, b) => b.length - a.length)[0];
  const link = (i: NavItem) =>
    i.children?.length ? (
      <NavGroup key={i.to} item={i} onNavigate={onNavigate} />
    ) : (
      <NavLink key={i.to} to={i.to} end={i.to === '/'} className={() => `nav-link ${i.to === current ? 'active' : ''}`} title={t(i.labelKey)} onClick={onNavigate}>
        {i.icon}
        <span className="label">{t(i.labelKey)}</span>
      </NavLink>
    );
  return (
    <>
      {AREAS.map((area) => {
        const inArea = items.filter((i) => i.area === area);
        if (!inArea.length) return null;
        return (
          <div key={area} className="nav-area">
            {area !== 'home' && (
              <div className="nav-section">
                <span className="nav-section-icon">{AREA_ICONS[area]}</span>
                <span>{t(`nav.area.${area}`)}</span>
              </div>
            )}
            {inArea.map(link)}
          </div>
        );
      })}
    </>
  );
}

function Brand() {
  const { t } = useTranslation();
  const h = useHospital().data;
  return (
    <div className="brand">
      <HospitalLogo hospitalId={h?.id} hasPhoto={h?.hasPhoto} version={h?.photoUpdatedAt} alt={h?.name} />
      <span>{t('app.name')}</span>
    </div>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const { canAny } = useAuth();
  const [drawer, setDrawer] = useState(false);
  const hospital = useHospital().data;
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const location = useLocation();
  // Phones: first four modules, but the current section always stays visible.
  const mainNav = visibleNav(canAny).filter((i) => i.area !== 'platform');
  const section = sectionOf(location.pathname);
  const current = mainNav.find((i) => i.to !== '/' && i.to === section);
  const bottom = mainNav.slice(0, 4);
  if (current && !bottom.includes(current)) bottom[bottom.length - 1] = current;

  return (
    <div className={`shell ${collapsed ? 'nav-collapsed' : ''}`}>
      <aside className="sidebar" aria-label={t('nav.menu')}>
        <Brand />
        <NavList />
      </aside>
      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="btn ghost sidebar-toggle"
            onClick={toggleCollapsed}
            aria-label={collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
            title={collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
            aria-expanded={!collapsed}
          >
            {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>
          <div className="mobile-brand">
            <HospitalLogo hospitalId={hospital?.id} hasPhoto={hospital?.hasPhoto} version={hospital?.photoUpdatedAt} alt={hospital?.name} size={30} />
            {t('app.name')}
          </div>
          <HospitalSwitcher />
          <div className="spacer" />
          <LanguageSwitcher />
          <ThemeSwitcher />
          <NotificationsBell />
          <UserMenu />
        </header>
        <main className="content" key={location.pathname.split('/')[1]}>
          <Outlet />
        </main>
      </div>
      <nav className="bottom-nav" aria-label={t('nav.menu')}>
        {bottom.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.to === '/'} className={() => (section === i.to || (i.to === '/' && location.pathname === '/') ? 'active' : '')}>
            {i.icon}
            <span>{t(i.labelKey)}</span>
          </NavLink>
        ))}
        <button onClick={() => setDrawer(true)} aria-label={t('common.more')}>
          <Menu size={20} />
          <span>{t('common.more')}</span>
        </button>
      </nav>
      {drawer && (
        <div className="drawer-backdrop" onClick={() => setDrawer(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <Brand />
            <NavList onNavigate={() => setDrawer(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
